import { Modal, modalWidth } from "@hubrise/react-components"
import * as React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import useCatalogNavigation from "Catalog/shared/hooks/useCatalogNavigation"
import { SkuId } from "shared/hubrise/ids"
import { IDeal } from "shared/hubrise/models"

import { Close, Description, Details, Edit, Footer, Name, Title, StyledPopup, Content, Header, Price } from "../Styles"
import Image from "../shared/components/Image"
import Sticky from "../shared/components/Sticky"

import DealLine from "./DealLine"
import useItemDeal from "./useItemDeal"

interface DealPopupProps {
  deal: IDeal
  rootElement: HTMLDivElement | undefined
}

const DealPopup = ({ deal, rootElement }: DealPopupProps): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const $modalRef = React.useRef<HTMLDivElement>(null)
  const $stickyHeaderRef = React.useRef<HTMLDivElement>(null)

  const { catalogNavigation } = useCatalogNavigation()
  const editPath = catalogNavigation.editPath("deal", deal.id)

  const { itemDealLines, price, setSkuId, setOptionNames } = useItemDeal(deal)
  const [expandedIndex, setExpandedIndex] = React.useState<number | null>(null)

  return (
    <Modal
      ref={$modalRef}
      rootElement={rootElement}
      modalWidth={modalWidth.xlarge}
      renderContent={({ close }) => (
        <StyledPopup>
          <Image productOrDeal={deal} />

          <Details>
            <Sticky position="top" ref={$stickyHeaderRef} $scrollableDivRef={$modalRef}>
              <Header>
                <Title>
                  <Name>{deal.name}</Name>
                  <Price dangerouslySetInnerHTML={{ __html: price.toHumanString() }} />
                </Title>
              </Header>
            </Sticky>

            <Content>
              <Description dangerouslySetInnerHTML={{ __html: deal.description }} />

              {itemDealLines.map((itemDealLine, index) => (
                <DealLine
                  key={index}
                  itemDealLine={itemDealLine}
                  setSkuId={(skuId: SkuId) => setSkuId(index, skuId)}
                  optionLists={itemDealLine.optionLists}
                  optionNames={(optionListName: string) => itemDealLine.optionNames(optionListName)}
                  setOptionNames={(optionListName: string, optionNames: Array<string>) =>
                    setOptionNames(index, optionListName, optionNames)
                  }
                  expanded={expandedIndex === index}
                  setExpanded={(expanded: boolean) => setExpandedIndex(expanded ? index : null)}
                  modalScrollableRef={$modalRef}
                  stickyHeaderRef={$stickyHeaderRef}
                />
              ))}
            </Content>

            <Sticky position="bottom" $scrollableDivRef={$modalRef}>
              <Footer>
                <Edit onClick={() => navigate(editPath)}>{t("preview.popup.deal.edit")}</Edit>
                <Close onClick={close}>{t("general.close")}</Close>
              </Footer>
            </Sticky>
          </Details>
        </StyledPopup>
      )}
      onClose={() => navigate(catalogNavigation.previewPath("all"))}
    />
  )
}

export default DealPopup
